import { AuthLayout } from '@/components/AuthLayout'
import { describeAuthError, errorCode } from '@/lib/authErrors'
import { createHonoClient } from '@/stores/api'
import { setSessionToken, setTenant } from '@/stores/session'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@repo/ui/components'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const searchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/invite')({
  component: InvitePage,
  validateSearch: searchSchema,
})

type ValidationResult = {
  type: 'internal_user' | 'contractor'
  tenant: { id: string; name: string; slug: string }
  user: { email: string; givenName: string | null; familyName: string | null }
  role: { slug: string; name: string }
  inviterName: string | null
  contractorCompanyName: string | null
  requiresPasswordSetup: boolean
}

// Unauthenticated client — the invite endpoints are public.
const api = createHonoClient(null)

function InviteError({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
      <AlertCircle className="size-4 shrink-0" />
      {message}
    </div>
  )
}

function InvitePage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const { token } = Route.useSearch()

  const validation = useQuery<ValidationResult, { message?: string }>({
    queryKey: ['invitation', token],
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      const res = await api.invitations.validate[':token'].$get({ param: { token: token! } })
      if (!res.ok) {
        throw { message: errorCode(await res.json().catch(() => null)) ?? 'INVITATION_NOT_FOUND' }
      }
      return res.json() as Promise<ValidationResult>
    },
  })

  const [givenName, setGivenName] = useState('')
  const [familyName, setFamilyName] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Seed the name fields from the invited user once the invitation resolves.
  // Carrier-owner accounts are provisioned nameless, so this is often blank and
  // the owner fills it in here.
  useEffect(() => {
    if (validation.data) {
      setGivenName(validation.data.user.givenName ?? '')
      setFamilyName(validation.data.user.familyName ?? '')
    }
  }, [validation.data])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (!token) return
    if (!givenName.trim() || !familyName.trim()) {
      setFormError(t('invite.errors.nameRequired'))
      return
    }
    const isSetup = validation.data?.requiresPasswordSetup ?? true
    if (isSetup) {
      if (newPassword !== confirmPassword) {
        setFormError(t('invite.errors.passwordsMismatch'))
        return
      }
      if (newPassword.length < 8) {
        setFormError(t('invite.errors.passwordTooShort'))
        return
      }
    } else if (newPassword.length < 1) {
      setFormError(t('invite.errors.currentPasswordRequired'))
      return
    }

    setIsSubmitting(true)
    try {
      const res = await api.invitations.accept.$post({
        json: {
          token,
          password: newPassword,
          givenName: givenName.trim(),
          familyName: familyName.trim(),
        },
      })
      if (!res.ok) {
        const code = errorCode(await res.json().catch(() => null))
        setFormError(describeAuthError(t, code, code ?? t('invite.errors.acceptFailed')))
        return
      }
      const data = await res.json()
      setSessionToken(data.sessionToken)
      setTenant({ id: data.tenant.id, name: data.tenant.name, slug: data.tenant.slug })
      navigate({ to: `/${data.tenant.slug}/quotes` })
    } catch (err) {
      console.error('Accept invitation failed:', err)
      setFormError(t('invite.errors.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const getInviteDescription = (invitation: ValidationResult) => {
    const values = {
      tenant: invitation.tenant.name,
      company: invitation.contractorCompanyName ?? t('invite.yourCompany'),
    }
    return invitation.inviterName
      ? t('invite.descriptionWithInviter', { ...values, inviter: invitation.inviterName })
      : t('invite.description', values)
  }

  const renderContent = () => {
    if (!token) return <InviteError message={t('invite.missingToken')} />
    if (validation.isPending) {
      return <p className="text-sm text-muted-foreground">{t('invite.validating')}</p>
    }
    if (validation.isError) {
      return (
        <InviteError
          message={describeAuthError(t, validation.error?.message, t('invite.invalidLink'))}
        />
      )
    }
    const { requiresPasswordSetup } = validation.data
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="given-name">{t('invite.givenName')}</Label>
          <Input
            id="given-name"
            value={givenName}
            onChange={(e) => setGivenName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="family-name">{t('invite.familyName')}</Label>
          <Input
            id="family-name"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password">
            {requiresPasswordSetup ? t('invite.newPassword') : t('invite.currentPassword')}
          </Label>
          <Input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={requiresPasswordSetup ? 8 : 1}
          />
        </div>
        {requiresPasswordSetup && (
          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('invite.confirmPassword')}</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
        )}

        {formError && <InviteError message={formError} />}

        <Button type="submit" className="w-full" isDisabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('invite.working')}
            </>
          ) : (
            t('invite.submit')
          )}
        </Button>
      </form>
    )
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t('invite.title')}</CardTitle>
          {validation.data && (
            <CardDescription>{getInviteDescription(validation.data)}</CardDescription>
          )}
        </CardHeader>
        <CardContent>{renderContent()}</CardContent>
      </Card>
    </AuthLayout>
  )
}
